import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { AppNotification } from '@/lib/notifications';

/**
 * One notification list, two surfaces.
 *
 * The topbar popup and the Notifications page render the same state, so marking
 * something read in one immediately clears the bell's dot in the other. Holding
 * it in a provider is what stops the two drifting into separate copies — the
 * usual way a "3 unread" badge ends up disagreeing with the list beneath it.
 *
 * The list is EMPTY until a read API exists — see `lib/notifications.ts` for why
 * the invented feed was removed rather than relabelled. The state and its
 * mutators stay because they are what the endpoint will drive: when it lands
 * this becomes a TanStack Query cache (`useQuery(['notifications'])` plus
 * mutations that invalidate it) and the consumers keep their shape.
 */

interface NotificationsContextValue {
  items: AppNotification[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
  /** Accept / decline an access request. */
  decide: (id: string, decision: 'accepted' | 'declined') => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<AppNotification[]>([]);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      items,
      unreadCount: items.filter((n) => n.unread).length,
      markRead: (id) =>
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, unread: false } : n))),
      markAllRead: () => setItems((prev) => prev.map((n) => ({ ...n, unread: false }))),
      decide: (id, decision) =>
        setItems((prev) =>
          prev.map((n) =>
            n.id === id ? { ...n, decision, requiresDecision: false, unread: false } : n,
          ),
        ),
    }),
    [items],
  );

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within <NotificationsProvider>');
  return ctx;
}
